import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useFieldArray, useForm, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Accordion from 'components/global/Accordion';
import Alert from 'components/global/Alert';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Icon from 'components/global/Icon';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';
import useUpdateEffect from 'hooks/useUpdateEffect';

import { AUTOCOMPLETE, AUTOMATION, ENUMS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { faArrowRightLong, faGripDotsVertical, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';

import AutomationConditionsTable, {
	AutomationConditionsConfig,
	convertAutomationConditionValue
} from './AutomationConditionsTable';
import * as S from './AutomationForm.styled';

const AutomationForm = NiceModal.create(({ id, mode, modalName }) => {
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					name: yup.string().nullable().required(),
					description: yup.string().nullable(),
					status: yup.string().nullable().required(),
					triggerEvent: yup.string().nullable().required(),
					changedFrom: yup.lazy((_, context) => {
						const eventConfig = triggerEventConfig.find(
							({ name }) => name === context?.parent?.triggerEvent
						);
						return (
							eventConfig?.fields.find(({ name }) => name === 'changedFrom')?.validationSchema ??
							yup.mixed().nullable()
						);
					}),
					changedTo: yup.lazy((_, context) => {
						const eventConfig = triggerEventConfig.find(
							({ name }) => name === context?.parent?.triggerEvent
						);
						return (
							eventConfig?.fields.find(({ name }) => name === 'changedTo')?.validationSchema ??
							yup.mixed().nullable()
						);
					}),
					conditions: yup
						.array()
						.of(
							yup.object().shape({
								condition: yup.string().nullable().required(),
								operator: yup
									.string()
									.nullable()
									.when('condition', ([condition], schema) => {
										const item = AutomationConditionsConfig.find((item) => item.name === condition);
										return item?.type ? schema.required() : schema;
									}),

								value: yup.mixed().nullable().required()
							})
						)
						.nullable(),
					actionConfigs: yup.array().of(
						yup.object().shape({
							name: yup.string().nullable().required(),
							actionType: yup.string().nullable().required(),
							failedRetry: yup.boolean(),
							exactlyOnce: yup.boolean(),
							functionTypeId: yup.number().nullable().required(),
							integrationId: yup.number().nullable().required(),
							parameterJson: yup.lazy((_, context) => {
								const config = context.parent?.parameterJsonConfig;
								const parameterSchema = yup.object().shape(
									config?.reduce((acc, { name, type, required }) => {
										if (type === 'string') {
											acc[name] = required
												? yup.string().nullable().required()
												: yup.string().nullable();
										}

										if (type === 'integer') {
											acc[name] = required
												? yup.number().nullable().required()
												: yup.number().nullable();
										}

										if (type === 'enum') {
											acc[name] = required
												? yup.string().nullable().required()
												: yup.string().nullable();
										}

										return acc;
									}, {})
								);
								return config ? parameterSchema : yup.mixed().nullable();
							})
						})
					)
				})
				.required(),
		[]
	);

	const viewMode = mode === 'view';

	const { closeModal } = useModalRouter();
	const modal = useModal();

	const handleCloseModal = () => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	};

	const { showError, showSuccess } = useAlert();

	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();

	const fillForm = useCallback(async () => {
		const defaultValues = {
			name: null,
			description: null,
			status: 'active',
			triggerEvent: null,
			changedFrom: null,
			changedTo: null,
			conditions: null,
			actionConfigs: [
				{
					name: null,
					actionType: null,
					failedRetry: false,
					exactlyOnce: false,
					functionTypeId: null,
					integrationId: null,
					parameterJson: null
				}
			]
		};
		if (!id) {
			return defaultValues;
		}

		try {
			const response = await axiosPrivate.post(AUTOMATION.GET_AUTOMATION_BY_ID, {
				id
			});

			const { data, status } = response;

			if (status !== 200) {
				showError();
				handleCloseModal();
				return;
			}

			return {
				...defaultValues,
				...data,
				changedFrom: data.changedFrom ? data.changedFrom.split(',') : null,
				changedTo: data.changedTo ? data.changedTo.split(',') : null,
				conditions: data.conditions.map((item) => {
					const parameter = AutomationConditionsConfig.find((condition) => condition.name === item.parameter);

					return {
						condition:
							AutomationConditionsConfig.find(
								(condition) =>
									condition?.nameV1 === item.parameter || condition?.nameV12 === item.parameter
							)?.name ?? item.parameter,
						operator: item.parameterCondition ?? null,
						value: convertAutomationConditionValue(item.parameterValue, parameter) ?? null
					};
				})
			};
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	}, []);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		setValue,
		watch,
		control,
		formState: { isDirty, isSubmitting, isSubmitSuccessful, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;
	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const onSubmit = async (data) => {
		const URL = id ? AUTOMATION.UPDATE_AUTOMATION : AUTOMATION.ADD_AUTOMATION;

		await axiosPrivate
			.post(URL, {
				id,
				...data,
				changedFrom: Array.isArray(data.changedFrom) ? data.changedFrom.toString() : data.changedFrom,
				changedTo: Array.isArray(data.changedTo) ? data.changedTo.toString() : data.changedTo,
				conditions: data.conditions
					? data.conditions.map((item) => ({
							parameter: item.condition,
							parameterCondition: item.operator === 'equalCustom' ? 'equal' : item.operator,
							parameterValue: Array.isArray(item.value)
								? item.value.map((value) => `${value.toString()}`).toString()
								: item.value.toString()
						}))
					: []
			})
			.then((response) => {
				const { status } = response;

				if (status !== 200) {
					showError();
					return;
				}

				showSuccess();
				dispatch(updateDynamicListData(DYNAMICLISTS.AUTOMATION));
				handleCloseModal();
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});
	};

	const defaultColSpan = {
		md: 6
	};

	const {
		fields: actionConfigFields,
		move: moveActionConfigFields,
		append: appendActionConfigField,
		remove: removeActionConfigField
	} = useFieldArray({
		control,
		name: 'actionConfigs'
	});

	return (
		<Modal fullwidth maxWidth="no-max-width" name={modalName} className="modal-automationForm">
			<ModalTitle>
				<Typography variant="p1">{viewMode ? 'view' : mode === 'edit' ? 'edit' : 'add'}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<S.RowContainer>
						<S.Wrapper>
							<FormItem label="name" colSpan={defaultColSpan} isLoading={isLoading} required>
								<StringInput name="name" disabled={viewMode} />
							</FormItem>

							<FormItem label="status" colSpan={defaultColSpan} isLoading={isLoading} required>
								<EnumSelect
									enumName={ENUMS.ACTIVE_INACTIVE}
									name="status"
									value={watch(`status`)}
									setValue={(value) => {
										setValue(`status`, value, { shouldValidate: true, shouldDirty: true });
									}}
									valueRemovable={false}
									searchable={false}
									disabled={viewMode}
								/>
							</FormItem>

							<FormItem label="description" isLoading={isLoading}>
								<StringInput name="description" disabled={viewMode} />
							</FormItem>
							<FormItem>
								<Alert showIcon content="automation.whenThisHappensAlert" />
							</FormItem>

							<AutomationTrigger viewMode={viewMode} />
							<FormItem label="feasibility.conditions">
								<AutomationConditionsTable
									isLoading={isLoading}
									conditionsEnumName={ENUMS.AUTOMATION_CONDITIONS}
									viewMode={viewMode}
									deletableLastRow={true}
								/>
							</FormItem>
						</S.Wrapper>
						<S.ArrowContainer>
							<S.Line />
							<Icon icon={faArrowRightLong} color="var(--black-40)" />
							<S.Line />
						</S.ArrowContainer>
						<S.ActionWrapper>
							<Alert showIcon content="automation.thenDoThisAlert" />
							<DndProvider backend={HTML5Backend}>
								{actionConfigFields?.map((field, index) => (
									<ActionDndItem
										key={`${field.id}${index}`}
										index={index}
										data={field}
										viewMode={viewMode}
										onMove={moveActionConfigFields}
										handleRemove={() => {
											removeActionConfigField(index);
										}}
										removable={actionConfigFields.length > 1}
									/>
								))}
							</DndProvider>
							{!viewMode && (
								<Button
									label="addNewAction"
									icon={faPlus}
									variant="blackOutline"
									onClick={() =>
										appendActionConfigField({
											name: null,
											actionType: null,
											failedRetry: false,
											exactlyOnce: false,
											functionTypeId: null,
											integrationId: null,
											parameterJson: null
										})
									}
									style={{ flex: '0 0 auto' }}
								/>
							)}
						</S.ActionWrapper>
					</S.RowContainer>
				</Form>
			</ModalContent>
			{!viewMode && (
				<ModalActions>
					<Button label="cancel" variant="outline" onClick={handleCloseModal} />
					<Button
						label="save"
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						loading={isSubmitting}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
							showError({
								message: 'error.missingRequiredFieldsHeader'
							});
						})}
						iconPos="right"
					/>
				</ModalActions>
			)}
		</Modal>
	);
});

const AutomationTrigger = ({ viewMode }) => {
	const { control, setValue, watch } = useFormContext();

	const [selectedConfig, setSelectedConfig] = useState(null);

	const watchedTriggerEvent = useWatch({
		control,
		name: `triggerEvent`
	});

	useUpdateEffect(() => {
		if (watchedTriggerEvent === selectedConfig?.name) {
			return;
		}

		setSelectedConfig(triggerEventConfig.find(({ name }) => name === watchedTriggerEvent));
	}, [watchedTriggerEvent]);

	const calculatedColSpan = useMemo(() => {
		if (!selectedConfig) {
			return undefined;
		}

		return {
			md: 12 / (selectedConfig?.fields?.length + 1)
		};
	}, [selectedConfig]);

	return (
		<>
			<FormItem label="whenThisHappens" colSpan={calculatedColSpan} required>
				<EnumSelect
					name="triggerEvent"
					value={watch('triggerEvent')}
					setValue={(value) => {
						setValue('triggerEvent', value, { shouldValidate: true, shouldDirty: true });
						setSelectedConfig(triggerEventConfig.find(({ name }) => name === value));
					}}
					data={triggerEventConfig.map(({ name }) => name)}
					valueRemovable={false}
					searchable={false}
					disabled={viewMode}
				/>
			</FormItem>

			{selectedConfig?.fields?.map(({ name, type, path, multipleSelection }, i) => (
				<FormItem key={i} label={name} colSpan={calculatedColSpan} required>
					{type === 'enum' ? (
						<EnumSelect
							name={name}
							enumName={path}
							value={watch(name)}
							setValue={(value) => {
								setValue(name, value, { shouldValidate: true, shouldDirty: true });
							}}
							valueRemovable={false}
							disabled={viewMode}
							multipleSelection={multipleSelection}
						/>
					) : (
						<StringInput name={name} disabled={viewMode} />
					)}
				</FormItem>
			))}
		</>
	);
};

const ActionDndItem = (props) => {
	const { index, data, viewMode, onMove, handleRemove, removable } = props;

	const id = data.id;

	const [, dropRef] = useDrop({
		accept: 'row',
		drop: (draggedRow) => onMove(draggedRow.index, index)
	});

	const [{ isDragging }, dragRef, previewRef] = useDrag({
		type: 'row',
		item: () => ({ id, index }),
		collect: (monitor) => ({
			isDragging: monitor.isDragging()
		})
	});

	return (
		<S.DndContainer ref={previewRef} style={{ opacity: isDragging ? 0.5 : 1 }}>
			{!viewMode && (
				<S.DndIconContainer ref={dropRef}>
					<Icon ref={dragRef} icon={faGripDotsVertical} size="large" color="var(--black)" />
				</S.DndIconContainer>
			)}
			<ActionItem index={index} viewMode={viewMode} />
			{!viewMode && (
				<>
					<S.RemoveButtonContainer>
						<Button
							variant="transparent"
							color="var(--red)"
							icon={faTrashCan}
							size="large"
							iconSize="medium"
							iconButton
							onClick={() => {
								NiceModal.show(ConfirmationModal, {
									title: 'automation.removeActionTitle',
									subTitle: 'automation.removeActionDescription',
									yesAction: handleRemove
								});
							}}
							disabled={!removable}
						/>
					</S.RemoveButtonContainer>
				</>
			)}
		</S.DndContainer>
	);
};

const ActionItem = ({ index, viewMode }) => {
	const { t } = useTranslation();
	const {
		watch,
		setValue,
		control,
		formState: { isSubmitting, isLoading: isFormLoading }
	} = useFormContext();
	const isLoading = isFormLoading || isSubmitting;

	const watchedActionType = useWatch({
		control,
		name: `actionConfigs.${index}.actionType`
	});

	const watchedParameterJsonConfig = useWatch({
		control,
		name: `actionConfigs.${index}.parameterJsonConfig`
	});

	const selectedActionType = useMemo(
		() => (watchedActionType ? actionConfig.find(({ name }) => name === watchedActionType) : null),
		[watchedActionType]
	);

	return (
		<Accordion
			header={`${t('automation.actionAccordionHeader', {
				index: index + 1,
				actionName: watch(`actionConfigs.${index}.name`)
			})}`}
			style={{ width: '100%', padding: '1rem' }}>
			<FormItem label="name" isLoading={isLoading} required>
				<StringInput name={`actionConfigs.${index}.name`} disabled={viewMode} />
			</FormItem>
			<FormItem label="type" isLoading={isLoading} required>
				<EnumSelect
					name={`actionConfigs.${index}.actionType`}
					value={watch(`actionConfigs.${index}.actionType`)}
					setValue={(value) => {
						setValue(`actionConfigs.${index}.actionType`, value, {
							shouldValidate: true,
							shouldDirty: true
						});
					}}
					data={actionConfig.map(({ name }) => name)}
					valueRemovable={false}
					searchable={false}
					disabled={viewMode}
				/>
			</FormItem>

			{selectedActionType?.fields?.map(
				({ name, label, type, path, columns, selectionLabelAccessor, filterObjectAccessor }, i) => (
					<FormItem key={i} label={label} required>
						{type === 'autocomplete' ? (
							<AutoComplete
								name={`actionConfigs.${index}.${name}`}
								autoCompleteName={path}
								value={watch(`actionConfigs.${index}.${name}`)}
								setValue={(value) => {
									setValue(`actionConfigs.${index}.${name}`, value?.ID, {
										shouldValidate: true,
										shouldDirty: true
									});

									if (name === 'functionTypeId') {
										const parameterJsonStr = value?.parameterJsonTemplate;
										setValue(
											`actionConfigs.${index}.parameterJsonConfig`,
											parameterJsonStr ? JSON.parse(parameterJsonStr) : null,
											{
												shouldValidate: true,
												shouldDirty: true
											}
										);
									}
								}}
								columns={columns}
								selectionLabelAccessor={selectionLabelAccessor}
								filterObject={
									filterObjectAccessor
										? {
												[filterObjectAccessor]: watch(
													`actionConfigs.${index}.${filterObjectAccessor}`
												)
											}
										: null
								}
								disabled={viewMode || watch(`actionConfigs.${index}.${filterObjectAccessor}`) === null}
							/>
						) : (
							<StringInput name={`actionConfigs.${index}.${name}`} disabled={viewMode} />
						)}
					</FormItem>
				)
			)}

			{watchedParameterJsonConfig?.map(({ name, type, required, enumName }, i) => (
				<FormItem key={i} label={name} required={required}>
					{type === 'enum' ? (
						<EnumSelect
							name={name}
							enumName={enumName}
							value={watch(`actionConfigs.${index}.parameterJson.${name}`)}
							setValue={(value) => {
								setValue(`actionConfigs.${index}.parameterJson.${name}`, value, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							valueRemovable={false}
							disabled={viewMode}
						/>
					) : (
						<StringInput name={`actionConfigs.${index}.parameterJson.${name}`} disabled={viewMode} />
					)}
				</FormItem>
			))}

			<FormItem
				label="automation.failedRetryLabel"
				description="automation.failedRetryDescription"
				isLoading={isLoading}
				direction="row-reverse">
				<SwitchInput name={`actionConfigs.${index}.failedRetry`} disabled={viewMode} />
			</FormItem>
			<FormItem
				label="automation.exactlyOnceLabel"
				description="automation.exactlyOnceDescription"
				isLoading={isLoading}
				direction="row-reverse">
				<SwitchInput name={`actionConfigs.${index}.exactlyOnce`} disabled={viewMode} />
			</FormItem>
		</Accordion>
	);
};

const triggerEventConfig = [
	{
		name: 'orderStatusChanged',
		fields: [
			{
				name: 'changedFrom',
				type: 'enum',
				path: ENUMS.AUTOMATION_TRIGGER_STATUSES,
				multipleSelection: true,
				validationSchema: yup.array().of(yup.string()).nullable().required()
			},
			{
				name: 'changedTo',
				type: 'enum',
				path: ENUMS.AUTOMATION_TRIGGER_STATUSES,
				multipleSelection: true,
				validationSchema: yup.array().of(yup.string()).nullable().required()
			}
		]
	}
];

const actionConfig = [
	{
		name: 'ecommerceUpdate',
		fields: [
			{
				name: 'integrationId',
				label: 'automation.integration',
				type: 'autocomplete',
				path: AUTOCOMPLETE.AUTOMATION_INTEGRATIONS,
				columns: [{ accessor: 'Name', width: 200 }],
				selectionLabelAccessor: ['Name']
			},
			{
				name: 'functionTypeId',
				label: 'automation.functionType',
				type: 'autocomplete',
				path: AUTOCOMPLETE.AUTOMATION_FUNCTION_TYPES,
				columns: [{ accessor: 'Name', width: 200 }],
				selectionLabelAccessor: ['Name'],
				filterObjectAccessor: 'integrationId'
			}
		]
	}
];

export default AutomationForm;
